/**
 * Раздел «Отказы» вкладки «Продажи» (#844, #2060): «Почему отказываются» и
 * один блок разбивки отказов с разрезом — номер звонка, сотрудник, стадия
 * (спека 2026-10-11-voice-report-layers.md §2.3). Разрез живёт в адресе
 * (`voiceCut`); «стадия» — только когда сервер прислал `byStage`.
 */
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import type { VoiceCustomerQuestions } from '@aica/api-types/schemas';
import { useCrmStageNames } from '@/hooks/useCrmStagesReference';
import { SegmentedButtons } from '@/components/ui/segmented-buttons';
import { DeclineBreakdownPanel } from './DeclineBreakdownPanel';
import { VoiceQuestionPanel } from './VoiceQuestionPanel';
import { VoiceQuestionsCoverage } from './VoiceQuestionsCoverage';
import { VoiceQuestionsNotes } from './VoiceQuestionsNotes';
import { VOICE_TOP_ROWS } from './VoiceTopList';
import {
  callNumberBreakdown,
  declineSection,
  operatorBreakdown,
  operatorBreakdownEmptyText,
  operatorBreakdownNotes,
  stageBreakdown,
} from './voice-questions-view-model';
import {
  VOICE_DECLINE_CUTS,
  type VoiceDeclineCut,
  type VoiceSearch,
} from './voice-search';

interface VoiceSalesDeclinesProps {
  cq: VoiceCustomerQuestions;
  search: VoiceSearch;
  onSearchChange: (patch: Partial<VoiceSearch>) => void;
  onOpenCall: (recordId: string) => void;
}

export function VoiceSalesDeclines({
  cq,
  search,
  onSearchChange,
  onOpenCall,
}: VoiceSalesDeclinesProps) {
  const { t } = useTranslation(['dashboard', 'records', 'common']);
  const cutLabelId = useId();
  const declines = cq.declines.callsInWindow;
  const cuts = VOICE_DECLINE_CUTS.filter(
    (id) => id !== 'stage' || cq.declines.byStage != null
  );
  // A cut the answer does not carry (a stage cut from an address of an
  // organization without stages) falls back to the first.
  const cut: VoiceDeclineCut =
    search.voiceCut && cuts.includes(search.voiceCut) ? search.voiceCut : 'callNumber';
  // Stage names for «reason × funnel stage», asked only when that cut is open
  // and the server sent the breakdown. The names of every CRM come at once
  // (`/crm/stage-names`, #2872) and each row takes the list of its own install:
  // `/crm/stages` describes the first install only. It answers in both shells:
  // the embedded router mounts it too, under the same permission check.
  const stages = useCrmStageNames({
    enabled: cut === 'stage' && Boolean(cq.declines.byStage?.length),
  });

  const namesUnavailable =
    (stages.isError && stages.data === undefined) ||
    (stages.data?.unavailable.length ?? 0) > 0;

  const breakdown = () => {
    switch (cut) {
      case 'operator':
        return (
          <DeclineBreakdownPanel
            title={t('voicePage.declines.byOperator.title')}
            hint={t('voicePage.declines.byOperator.hint')}
            rows={operatorBreakdown(cq, t)}
            declines={declines}
            onOpenCall={onOpenCall}
            emptyText={operatorBreakdownEmptyText(cq, t)}
            notes={operatorBreakdownNotes(cq, t)}
            limit={VOICE_TOP_ROWS}
          />
        );
      case 'stage':
        return (
          <DeclineBreakdownPanel
            title={t('voicePage.declines.byStage.title')}
            hint={[
              t('voicePage.declines.byStage.hint'),
              // A CRM did not answer (none, or one of several) and no earlier list
              // is kept: rows read by phase and id — said so, not left unexplained.
              namesUnavailable ? t('voicePage.declines.byStage.namesUnavailable') : '',
            ]
              .filter(Boolean)
              .join(' ')}
            rows={stageBreakdown(cq, stages.data, t) ?? []}
            declines={declines}
            onOpenCall={onOpenCall}
            limit={VOICE_TOP_ROWS}
          />
        );
      default:
        // The call-number buckets are fixed: never cut to five.
        return (
          <DeclineBreakdownPanel
            title={t('voicePage.declines.byCallNumber.title')}
            hint={t('voicePage.declines.byCallNumber.hint')}
            rows={callNumberBreakdown(cq, t)}
            declines={declines}
            onOpenCall={onOpenCall}
          />
        );
    }
  };

  return (
    <div className="space-y-4">
      <VoiceQuestionsCoverage cq={cq} />
      <VoiceQuestionPanel
        section={declineSection(cq)}
        cq={cq}
        search={search}
        onSearchChange={onSearchChange}
        onOpenCall={onOpenCall}
        hint={t('voicePage.declines.hint')}
        limit={VOICE_TOP_ROWS}
      />
      {declines > 0 && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-fg-muted" id={cutLabelId}>
              {t('voicePage.layers.cutLabel')}
            </span>
            <SegmentedButtons
              labelledBy={cutLabelId}
              options={cuts.map((id) => ({ id, label: t(`voicePage.layers.cut.${id}`) }))}
              value={cut}
              onChange={(id) =>
                onSearchChange({ voiceCut: id === 'callNumber' ? undefined : id })
              }
            />
          </div>
          {/* Its own key per cut: an open cell belongs to the cut it was opened in. */}
          <div key={cut}>{breakdown()}</div>
        </div>
      )}
      <VoiceQuestionsNotes cq={cq}>
        <p>{t('voicePage.declines.denominatorNote')}</p>
      </VoiceQuestionsNotes>
    </div>
  );
}
